import { type ReactNode, useCallback } from "react";
import { useSelector } from "react-redux";
import type {
  FareDetailsV2,
  FareSliceDetailsV2,
  TripDetailsV2,
} from "@b2bportal/air-shopping-api";
import { getAirports, getSelectedTripDetails } from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { DateTimeFormatStyle, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { FlightSegmentCard } from "../FlightSegmentCard";
import styles from "./styles.module.scss";

export const FlightBookingDetails = ({
  tripDetails: propTripDetails,
  fareDetails,
  renderSliceFooter,
}: {
  tripDetails?: TripDetailsV2;
  // selected fare; when provided, each segment shows its cabin
  fareDetails?: FareDetailsV2;
  renderSliceFooter?: (fareSlice: FareSliceDetailsV2) => ReactNode;
}) => {
  const [block, cn] = useModuleBEM(styles, "FlightBookingSummary");
  const { formatDateTime } = useI18nContext();

  const shopTripDetails = useSelector(getSelectedTripDetails);
  const airports = useSelector(getAirports);
  const tripDetails = propTripDetails ?? shopTripDetails;

  const getLocationString = useCallback(
    (airportCode: string) => {
      return airports?.[airportCode]
        ? `${airports[airportCode]?.cityName} (${airportCode})`
        : airportCode;
    },
    [airports]
  );

  return (
    <div className={block}>
      {tripDetails?.slices?.map((slice, sliceIndex) => {
        const fareSlice = fareDetails?.slices[sliceIndex];
        return (
          <div className={cn("slice")} key={slice.id}>
            <div className={cn("slice-title")}>
              <Trans
                i18nKey={"flightReview.itineraryTitle"}
                values={{
                  fromCity: getLocationString(slice.originCode),
                  toCity: getLocationString(slice.destinationCode),
                  date: formatDateTime(
                    dayjs(removeTimezone(slice.departureTime)).toDate(),
                    DateTimeFormatStyle.ShortMonthDayShortWeekday
                  ),
                }}
                components={{ 1: <span /> }}
              />
            </div>

            {slice.segmentDetails.map((segment, segmentIndex) => (
              <FlightSegmentCard
                {...segment}
                cabinClassName={
                  fareSlice?.fareDetails.segments[segmentIndex]?.cabinClassName
                }
                className={cn("flight-segment")}
                key={`${segment.originCode}-${segment.destinationCode}`}
              />
            ))}
            {fareSlice != null ? renderSliceFooter?.(fareSlice) : null}
          </div>
        );
      })}
    </div>
  );
};
